import React, { useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle2, ChevronRight, Clock, Download, ExternalLink, GitFork } from 'lucide-react';
import type { PortalFileRelease, ProjectFile, ProjectFileStream } from '../../types';
import {
  buildSubmittalEntries,
  buildSubmittalPhaseGroups,
  countCurrentSubmittalEntries,
  type SubmittalEntry,
} from '../../utils/projectSubmittals';
import {
  findClientApprovalForFileReleases,
  formatApprovalBadge,
  type PortalApprovalTargetGroup,
} from '../../utils/portalDeliverableApprovalService';
import { structureSelectionTitle } from '../../utils/structureSelectionLabel';
import { trackKindShort } from './projectUploadModalShared';
import './ProjectFiles.css';
import './ProjectFilesViews.css';

export interface ProjectSubmittalsViewProps {
  projectId?: string;
  releases: PortalFileRelease[];
  files: ProjectFile[];
  streams: ProjectFileStream[];
  dealApprovals: PortalApprovalTargetGroup;
  portalLinked: boolean;
  /** Open the hub source version (keeps comments attached). Falls back to the released copy. */
  onOpenSource: (file: ProjectFile) => void;
  onOpenStream: (streamId: string) => void;
  /** Optional toolbar slot (e.g. the "Client view" trail pop-out button). */
  toolbarExtra?: React.ReactNode;
}

function formatSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ProjectSubmittalsView: React.FC<ProjectSubmittalsViewProps> = ({
  releases,
  files,
  streams,
  dealApprovals,
  portalLinked,
  onOpenSource,
  onOpenStream,
  toolbarExtra,
}) => {
  const entries = useMemo(() => buildSubmittalEntries({ releases, files, streams }), [releases, files, streams]);
  const phaseGroups = useMemo(
    () => buildSubmittalPhaseGroups({ releases, files, streams }),
    [releases, files, streams],
  );
  const currentCount = useMemo(() => countCurrentSubmittalEntries(entries), [entries]);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) =>
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (!portalLinked) {
    return (
      <div className="pfv-empty" data-tour="pf-submittals">
        <p className="pfv-empty-title">No client portal linked</p>
        <p className="pfv-empty-hint">
          Submittals are the drawings and 3D models released to the client portal. Link a portal to this
          project and release a version from Deliverables to see them here.
        </p>
      </div>
    );
  }

  if (releases.length === 0) {
    return (
      <div className="pfv-empty" data-tour="pf-submittals">
        <p className="pfv-empty-title">Nothing released to the client yet</p>
        <p className="pfv-empty-hint">
          Use <strong>Release to client portal</strong> on a 2D or HTML track in Deliverables. Each release
          shows up here grouped by design phase.
        </p>
      </div>
    );
  }

  const renderEntry = (e: SubmittalEntry, opts?: { hideScope?: boolean }) => {
    const r = e.release;
    const approval =
      r.approval?.status === 'approved' ? r.approval : findClientApprovalForFileReleases(dealApprovals, [r]);
    const approvalText = formatApprovalBadge(approval);
    const awaiting = !approvalText && r.approvalRequested && !e.superseded && e.isCurrent;
    const scopeNames = e.scopes.flatMap(s => s.structureNames);
    return (
      <li
        key={r.id ?? `${r.sourceFileId}-${r.releaseVersionIndex}`}
        className={`pfv-row${e.isCurrent ? ' pfv-row--current' : ''}${e.superseded ? ' pfv-row--superseded' : ''}`}
      >
        <span className="pfv-row-kind" aria-hidden>
          <span className={`pf-stream-kind-badge pf-stream-kind-badge--col pf-stream-kind-${r.streamKind}`}>
            {trackKindShort(r.streamKind)}
          </span>
        </span>
        <div className="pfv-row-main">
          <div className="pfv-row-title-line">
            <span className="pfv-row-title" title={r.name}>
              {r.displayLabel || r.name}
            </span>
            <span className="pfv-chip pfv-chip--version">v{r.releaseVersionIndex}</span>
            {e.isCurrent ? <span className="pfv-chip pfv-chip--current">On portal</span> : null}
            {approvalText ? (
              <span className="pfv-chip pfv-chip--approved" title={approvalText}>
                <CheckCircle2 size={10} aria-hidden /> Client approved
              </span>
            ) : awaiting ? (
              <span className="pfv-chip pfv-chip--pending" title="Approval requested — waiting on the client">
                <Clock size={10} aria-hidden /> Awaiting approval
              </span>
            ) : null}
            {e.superseded ? (
              <span className="pfv-chip pfv-chip--lod" title="Replaced by a newer release before the client approved">
                Superseded
              </span>
            ) : null}
            {e.isWholeProjectRoot ? (
              <span className="pfv-chip pfv-chip--whole" title="Released before the project was split into structures">
                <GitFork size={10} aria-hidden /> Whole project
              </span>
            ) : null}
            {!opts?.hideScope && !e.isWholeProjectRoot
              ? scopeNames.map(n => (
                  <span key={n} className="pfv-chip pfv-chip--structure" title={structureSelectionTitle(scopeNames)}>
                    {n}
                  </span>
                ))
              : null}
          </div>
          <div className="pfv-row-meta">
            <button type="button" className="pfv-link" onClick={() => onOpenStream(r.sourceStreamId)}>
              {e.streamTitle}
            </button>
            {r.versionLabel ? <> · {r.versionLabel}</> : null}
            {r.versionIndex != null ? <> · #{r.versionIndex}</> : null}
            {' · '}
            {r.releasedByName || 'Unknown'} · {formatDistanceToNow(r.releasedAt, { addSuffix: true })}
            {r.size ? <> · {formatSize(r.size)}</> : null}
          </div>
        </div>
        <div className="pfv-row-acts">
          {e.sourceFile ? (
            <button type="button" className="pfv-act" onClick={() => onOpenSource(e.sourceFile!)}>
              Open
            </button>
          ) : (
            <a className="pfv-act" href={r.releasedUrl} target="_blank" rel="noopener noreferrer">
              Open <ExternalLink size={11} aria-hidden />
            </a>
          )}
          <a
            className="pfv-act pfv-act--muted"
            href={r.releasedUrl}
            download={r.name}
            target="_blank"
            rel="noopener noreferrer"
            title="Download the exact copy the client received"
          >
            <Download size={11} aria-hidden /> Client copy
          </a>
        </div>
      </li>
    );
  };

  return (
    <div className="pfv" data-tour="pf-submittals">
      <div className="pfv-summary">
        <span className="pfv-stat">{releases.length} release{releases.length !== 1 ? 's' : ''}</span>
        <span className="pfv-stat pfv-stat--active">{currentCount} on portal now</span>
        <span className="pfv-summary-note">
          Read-only — derived from what has been released to the client portal. Release or un-release from
          Deliverables.
        </span>
        <div className="pfv-summary-spacer" />
        {toolbarExtra}
      </div>

      {phaseGroups.map(g => {
        const key = `phase:${g.phase}`;
        const open = !collapsed.has(key);
        return (
          <section key={key} className="pfv-group">
            <button type="button" className="pfv-group-toggle" aria-expanded={open} onClick={() => toggle(key)}>
              <ChevronRight size={14} className={`pfv-caret${open ? ' pfv-caret--open' : ''}`} aria-hidden />
              <span className="pfv-group-title">{g.label}</span>
              <span className="pfv-group-count">{g.total}</span>
            </button>
            {open ? (
              <div className="pfv-group-body">
                {g.scopes.map(scope => (
                  <div key={scope.key} className="pfv-scope">
                    {g.scopes.length > 1 ? (
                      <div className="pfv-scope-head">
                        <span className="pfv-scope-title">{scope.label}</span>
                        <span className="pfv-group-count">{scope.total}</span>
                      </div>
                    ) : null}
                    <ul className="pfv-list">
                      {scope.media.flatMap(m => m.entries).map(e => renderEntry(e, { hideScope: g.scopes.length > 1 }))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
};

export default ProjectSubmittalsView;
